'use client'

import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useStore, useUpdateStore, StoreDetails } from '@/lib/api/stores'
import { trpc } from '@/lib/api/trpc'
import { ErrorDisplay } from '@/components/ui/error-display'
import { parseTRPCError } from '@/lib/utils/trpc-error-utils'
import { StoreForm, type StoreFormData, DeleteStoreBox } from '@/components/form/store'
import { StoreFormHeader } from '@/app/stores/create/components/store-form-header'
import { useToast } from '@/components/ui/use-toast'
import currencies from '@/data/currencies.json'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { useEffectiveBranding } from '@/hooks/use-effective-branding'
import { useEffectiveLogoUrl } from '@/hooks/use-effective-logo-url'

const availableCurrencies = Object.keys(currencies)

const StoreEditSkeleton = () => {
    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <div className="flex items-center gap-4">
                    <div className="space-y-2">
                        <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
                        <div className="h-4 w-64 animate-pulse rounded bg-gray-200" />
                    </div>
                </div>
                <div className="mt-6 grid gap-6 lg:grid-cols-3">
                    <div className="space-y-6 lg:col-span-2">
                        <div className="h-64 animate-pulse rounded-lg bg-gray-200" />
                        <div className="h-96 animate-pulse rounded-lg bg-gray-200" />
                    </div>
                    <div className="h-48 animate-pulse rounded-lg bg-gray-200" />
                </div>
            </div>
        </div>
    )
}

const StoreEditPageContainerInner = () => {
    const { id: storeId } = useParams()
    const { t } = useTranslation(['stores', 'common'])

    const { data: store, isLoading, error } = useStore(storeId || '', !!storeId) as {
        data: StoreDetails | null | undefined
        isLoading: boolean
        error: unknown
    }
    if (!storeId) {
        return null
    }
    if (isLoading) {
        return <StoreEditSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={parseTRPCError(error)} />
    }
    if (!store) {
        return (
            <div className="container mx-auto py-8">
                <p>{t('storeNotFound')}</p>
            </div>
        )
    }

    return <StoreEditForm store={store} storeId={storeId} />
}

const transformStoreToFormData = (store: StoreDetails, defaultPrimaryColor: string): StoreFormData => {
    const chargeCurrency = store.chargeCurrencies?.[0] || store.baseCurrency || ''

    const config = store.config as { primaryColor?: string; logoUrl?: string; currencyRateOverrides?: Record<string, number> } | null

    // Serialize overrides as "USD=1, GBP=0.85" for the text input
    const overridesObj = config?.currencyRateOverrides ?? {}
    const currencyRateOverridesText = Object.entries(overridesObj)
        .map(([k, v]) => `${k.toUpperCase()}=${v}`)
        .join(', ')

    return {
        name: store.name || '',
        presentmentCurrencies: store.presentmentCurrencies || [],
        chargeCurrency,
        baseCurrency: store.baseCurrency || '',
        multipleSubscriptionsPerCustomerEnabled: store.multipleSubscriptionsPerCustomerEnabled ?? true,
        customThankYouUrl: store.customThankYouUrl || undefined,
        primaryColor: config?.primaryColor || defaultPrimaryColor,
        logoUrl: config?.logoUrl || '',
        applyExternalBXGYDiscountOnSubscription: store.applyExternalBXGYDiscountOnSubscription ?? false,
        digitalProduct: store.digitalProduct ?? false,
        currencyRateOverrides: currencyRateOverridesText,
    }
}

const StoreEditForm = ({ store, storeId }: { store: StoreDetails; storeId: string }) => {
    const { t } = useTranslation(['stores', 'common'])
    const { storeRoutes, orgRoutes, navigate } = useStoreNavigate();
    const { toast } = useToast()
    const brand = useEffectiveBranding()
    const defaultLogoUrl = useEffectiveLogoUrl()
    const updateStoreMutation = useUpdateStore()
    const [formError, setFormError] = React.useState<ReturnType<typeof parseTRPCError> | null>(null)

    const { data: latestCurrencyTask } = trpc.tasks.getLatestByStoreAndType.useQuery(
        { storeId, type: 'store_add_currency' },
        {
            refetchInterval: (query) => {
                const status = query.state.data?.status
                return status === 'pending' || status === 'processing' ? 3000 : false
            },
        },
    )
    const hasPendingCurrencyTask = latestCurrencyTask?.status === 'pending'
        || latestCurrencyTask?.status === 'processing'

    const initialData = transformStoreToFormData(store, brand.primaryColor)

    const handleUpdateStore = async (data: StoreFormData) => {
        try {
            setFormError(null)

            // Parse "USD=1, GBP=0.85" → { USD: 1, GBP: 0.85 }
            const parsedOverrides: Record<string, number> = {}
            if (data.currencyRateOverrides) {
                for (const part of data.currencyRateOverrides.split(',')) {
                    const [currency, rateStr] = part.trim().split('=')
                    const rate = parseFloat(rateStr)
                    if (currency && !isNaN(rate)) {
                        parsedOverrides[currency.trim().toUpperCase()] = rate
                    }
                }
            }

            const existingConfig = (store.config || {}) as Record<string, unknown>
            await updateStoreMutation.mutateAsync({
                storeId,
                updatedData: {
                    name: data.name,
                    presentmentCurrencies: data.presentmentCurrencies,
                    chargeCurrencies: [data.chargeCurrency],
                    baseCurrency: data.baseCurrency,
                    type: store.type || 'shopify',
                    credentials: store.credentials || null,
                    multipleSubscriptionsPerCustomerEnabled: data.multipleSubscriptionsPerCustomerEnabled,
                    customThankYouUrl: data.customThankYouUrl && data.customThankYouUrl.length > 0 ? data.customThankYouUrl : null,
                    config: {
                        ...existingConfig,
                        primaryColor: data.primaryColor || brand.primaryColor,
                        logoUrl: data.logoUrl || defaultLogoUrl,
                        currencyRateOverrides: Object.keys(parsedOverrides).length > 0 ? parsedOverrides : undefined,
                    },
                    applyExternalBXGYDiscountOnSubscription: data.applyExternalBXGYDiscountOnSubscription,
                    digitalProduct: data.digitalProduct,
                },
            })

            toast({
                title: t('storeUpdated'),
                description: t('storeUpdatedSuccess'),
            })

            navigate(storeRoutes.STORE_BASE())
        } catch (error) {
            const parsedError = parseTRPCError(error)
            setFormError(parsedError)

            if (!parsedError.isValidationError) {
                toast({
                    title: t('updateError'),
                    description: parsedError.message,
                    variant: 'destructive',
                })
            }
        }
    }

    const handleCancel = () => {
        navigate(storeRoutes.STORE_BASE())
    }

    const isSubmitting = updateStoreMutation.isPending || hasPendingCurrencyTask

    const getSubmitLabel = () => {
        if (hasPendingCurrencyTask) {
            return t('currencyUpdateInProgress')
        }
        if (updateStoreMutation.isPending) {
            return t('saving')
        }
        return t('saveChanges')
    }

    const header = (
        <StoreFormHeader
            title={t('editStore')}
            description={t('editStoreDescription')}
            onCancel={handleCancel}
            onSubmit={() => { }}
            submitLabel={getSubmitLabel()}
            cancelLabel={t('cancel')}
            isSubmitting={isSubmitting}
        />
    )

    return (
        <StoreForm
            defaultValues={initialData}
            baseCurrencyEditable={false}
            currencies={availableCurrencies}
            onSubmit={handleUpdateStore}
            formError={formError}
            onDismissError={() => setFormError(null)}
            header={header}
            basicInformationTitle={t('basicInformation')}
            currenciesTitle={t('currencies')}
            settingsTitle={t('settings')}
            showDemoSeeding={false}
            dangerZone={
                <DeleteStoreBox
                    storeId={storeId}
                    storeName={store.name || ''}
                    onDeleted={() => navigate(orgRoutes.STORES())}
                />
            }
        />
    )
}

export const StoreEditPageContainer = withRequiredPermission(StoreEditPageContainerInner, PERMISSIONS.STORES_MANAGE)
export default StoreEditPageContainer

